import React, { createContext, useContext, useMemo, useRef, useState, useCallback } from "react";
import { CheckCircle, AlertTriangle, Info, XCircle, X } from "lucide-react";

/**
 * Usage:
 *  <NotificationSystem>
 *    <YourApp/>
 *  </NotificationSystem>
 *
 *  const { showNotification } = useNotification();
 *  showNotification("Pre-order item added to cart!", "success");
 *
 * Types: "success" | "warning" | "error" | "info"
 */

const NotificationContext = createContext({
  showNotification: (_msg, _type = "info", _opts = {}) => {},
  hideNotification: (_id) => {},
  clearAll: () => {},
});

export const useNotification = () => useContext(NotificationContext);

const TYPE_STYLES = {
  success: {
    container: "bg-green-600 border-green-700",
    Icon: CheckCircle,
  },
  warning: {
    container: "bg-yellow-500 border-yellow-600",
    Icon: AlertTriangle,
  },
  error: {
    container: "bg-red-600 border-red-700",
    Icon: XCircle,
  },
  info: {
    container: "bg-blue-600 border-blue-700",
    Icon: Info,
  },
};

export default function NotificationSystem({ children }) {
  const [toasts, setToasts] = useState([]); // [{id, message, type, timeout}]
  const timersRef = useRef({}); // id -> timeout handle

  const hideNotification = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
  }, []);

  const showNotification = useCallback((message, type = "info", opts = {}) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const timeout =
      typeof opts.timeout === "number"
        ? opts.timeout
        : type === "error"
        ? 6000
        : type === "warning"
        ? 5000
        : 3500;

    setToasts((prev) => [...prev, { id, message, type, timeout }]);

    // start per-toast timer once (avoids duplicate timers on re-renders)
    timersRef.current[id] = setTimeout(() => {
      hideNotification(id);
    }, timeout);

    return id;
  }, [hideNotification]);

  const clearAll = useCallback(() => {
    Object.values(timersRef.current).forEach(clearTimeout);
    timersRef.current = {};
    setToasts([]);
  }, []);

  const value = useMemo(
    () => ({ showNotification, hideNotification, clearAll }),
    [showNotification, hideNotification, clearAll]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}

      {/* Toast container */}
      <div className="fixed z-[60] top-4 right-4 space-y-3 pointer-events-none">
        {toasts.map((t) => {
          const style = TYPE_STYLES[t.type] || TYPE_STYLES.info;
          const Icon = style.Icon;
          return (
            <div
              key={t.id}
              className={`pointer-events-auto max-w-xs w-80 rounded-xl shadow-lg p-4 text-white border ${style.container} transition transform`}
              role="status"
              aria-live="polite"
            >
              <div className="flex items-start gap-3">
                <Icon size={20} className="mt-0.5 flex-shrink-0" />
                <p className="text-sm leading-5 flex-1">{t.message}</p>
                <button
                  onClick={() => hideNotification(t.id)}
                  className="ml-2 text-white/80 hover:text-white"
                  aria-label="Dismiss notification"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </NotificationContext.Provider>
  );
}
